// Ocean dashboard (HFL / MBL) için ortak, sade kurumsal UI parçaları
import React, { useState } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { Doughnut } from "react-chartjs-2";
import dayjs from "dayjs";
import { TrendingUp, TrendingDown, Minus, CalendarClock, PanelRightOpen, Download, AlertTriangle } from "lucide-react";
import MuiTextField from "@mui/material/TextField";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip as ChartTooltip } from "chart.js";
import TextField from "components/BufferedTextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, ChartTooltip);

export const UI = {
    navy: "#0B1F3A",
    text: "#0F172A",
    muted: "#64748B",
    subtle: "#94A3B8",
    border: "#E2E8F0",
    // Kart dış çizgisi: tema Card kenarlığıyla aynı ton
    cardBorder: "rgba(15, 23, 42, 0.12)",
    surface: "#FFFFFF",
    background: "#F5F7FA",
    teal: "#0E9F9A",
    blue: "#2563EB",
    orange: "#D97706",
    red: "#DC2626",
    green: "#16A34A",
    slate: "#64748B",
};

// Ton → renk eşlemesi (renk her zaman metinle birlikte kullanılır)
export const TONES = {
    neutral: { fg: "#334155", bg: "#F1F5F9" },
    info: { fg: "#1D4ED8", bg: "#EFF6FF" },
    teal: { fg: "#0F766E", bg: "#ECFDF5" },
    success: { fg: "#15803D", bg: "#F0FDF4" },
    warning: { fg: "#B45309", bg: "#FFFBEB" },
    danger: { fg: "#B91C1C", bg: "#FEF2F2" },
};

export const toneColor = (tone) =>
    ({
        neutral: UI.slate,
        info: UI.blue,
        teal: UI.teal,
        success: UI.green,
        warning: UI.orange,
        danger: UI.red,
    }[tone] || UI.slate);

export const PRIORITY_RANK = { Critical: 0, High: 1, Medium: 2, Normal: 3 };

export const priorityTone = (priority) =>
    ({ Critical: "danger", High: "warning", Medium: "info", Normal: "success" }[priority] || "neutral");

export const currencyOptions = ["EUR", "USD", "GBP", "TRY"];

export const safeNumber = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number : 0;
};

export const formatMoney = (value, currency = "EUR") =>
    `${safeNumber(value).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currency}`;

export const shortLabel = (label, max = 18) => {
    const text = String(label || "Unknown");
    return text.length > max ? `${text.slice(0, max)}…` : text;
};

// Kurlar tüm dashboard'lar için tek istekle alınır ve 12 saat tarayıcıda saklanır
const RATES_CACHE_KEY = "tms.exchangeRates.v1";
const RATES_TTL_MS = 12 * 60 * 60 * 1000;
let ratesPromise = null;

const readCachedRates = () => {
    try {
        const cached = JSON.parse(localStorage.getItem(RATES_CACHE_KEY) || "null");
        return cached && Date.now() - cached.savedAt < RATES_TTL_MS ? cached.data : null;
    } catch {
        return null;
    }
};

export const fetchExchangeRates = () => {
    if (ratesPromise) return ratesPromise;

    ratesPromise = (async () => {
        const cached = readCachedRates();
        if (cached) return cached;

        try {
            const res = await fetch("https://api.exchangerate-api.com/v4/latest/USD");
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            if (!data?.rates) throw new Error("No rates in response");
            try {
                localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data }));
            } catch {
                // depolama kapalıysa yalnızca bellekte tut
            }
            return data;
        } catch (error) {
            console.error("Exchange Rate API Error:", error);
            ratesPromise = null; // sonraki açılışta tekrar denensin
            return null;
        }
    })();

    return ratesPromise;
};

// Tablo hücreleri React bileşeni olduğunda react-table metin üzerinden yanlış sıralar.
// Satırdaki _sort[columnId] ham değeri (sayı / zaman damgası / metin) kullanılır.
const rawSort = (rowA, rowB, columnId) => {
    const a = rowA.original?._sort?.[columnId] ?? rowA.values[columnId];
    const b = rowB.original?._sort?.[columnId] ?? rowB.values[columnId];
    const empty = (v) => v === null || v === undefined || v === "" || v === "—";
    if (empty(a) && empty(b)) return 0;
    if (empty(a)) return -1;
    if (empty(b)) return 1;
    if (typeof a === "number" && typeof b === "number") return a === b ? 0 : a > b ? 1 : -1;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

export const withRawSort = (columns) => columns.map((c) => ({ ...c, sortType: c.sortType || rawSort }));

export const dateValue = (d) => (d ? dayjs(d).valueOf() : null);

export const DATE_PRESETS = [
    { value: "all", label: "All time" },
    { value: "thisMonth", label: "This month" },
    { value: "last30", label: "Last 30 days" },
    { value: "next30", label: "Next 30 days" },
    { value: "last90", label: "Last 90 days" },
    { value: "thisYear", label: "This year" },
    { value: "custom", label: "Custom range" },
];

export const resolveDateRange = ({ preset, from, to }) => {
    const today = dayjs().startOf("day");
    switch (preset) {
        case "thisMonth":
            return [today.startOf("month"), today.endOf("month")];
        case "last30":
            return [today.subtract(30, "day"), today.endOf("day")];
        case "next30":
            return [today, today.add(30, "day").endOf("day")];
        case "last90":
            return [today.subtract(90, "day"), today.endOf("day")];
        case "thisYear":
            return [today.startOf("year"), today.endOf("year")];
        case "custom":
            return [from ? dayjs(from).startOf("day") : null, to ? dayjs(to).endOf("day") : null];
        default:
            return [null, null];
    }
};

// Tarih aralığı durumu; aralık seçiliyken tarihi olmayan dosyalar listeden çıkar
export const useDateRange = () => {
    const [dateRange, setDateRange] = useState({ preset: "all", from: "", to: "" });
    const [start, end] = resolveDateRange(dateRange);
    const active = Boolean(start || end);

    const inRange = (date) => {
        if (!active) return true;
        if (!date) return false;
        const d = dayjs(date);
        if (start && d.isBefore(start)) return false;
        if (end && d.isAfter(end)) return false;
        return true;
    };

    return { dateRange, setDateRange, inRange, dateRangeActive: active };
};

// Excel'de Türkçe karakterler ve ; ayırıcı ile doğru açılan CSV indirir
export const downloadCsv = (filename, columns, rows) => {
    const escape = (v) => {
        const text = v === null || v === undefined ? "" : String(v);
        return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const lines = [
        columns.map(([header]) => escape(header)).join(";"),
        ...rows.map((row) => columns.map(([, get]) => escape(get(row))).join(";")),
    ];
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}-${dayjs().format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

export const csvDate = (d) => (d ? dayjs(d).format("DD.MM.YYYY") : "");
export const csvMoney = (v) => (Number.isFinite(v) ? v.toFixed(2).replace(".", ",") : "");

export const convertCurrency = (amount, fromCurrency, toCurrency, rates) => {
    const value = safeNumber(amount);

    if (!value) return 0;
    if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) return value;
    if (!rates?.rates) return value;

    const fromRate = rates.rates[fromCurrency];
    const toRate = rates.rates[toCurrency];

    if (!fromRate || !toRate) return value;

    return (value / fromRate) * toRate;
};

const cardSx = {
    borderRadius: 2,
    border: `1px solid ${UI.cardBorder}`,
    boxShadow: "none",
    backgroundColor: UI.surface,
};

// Uzun metni max karakterde keser, tamamını tooltip ile gösterir
export function TruncatedText({ text, max = 40 }) {
    const value = String(text ?? "—");
    if (value.length <= max) return value;

    return (
        <Tooltip title={value} arrow placement="top">
            <MDBox component="span" sx={{ cursor: "default" }}>
                {value.slice(0, max).trimEnd()}…
            </MDBox>
        </Tooltip>
    );
}

export function StatusPill({ label, tone = "neutral" }) {
    const t = TONES[tone] || TONES.neutral;

    return (
        <MDBox
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: 11.5,
                fontWeight: 600,
                lineHeight: 1.6,
                whiteSpace: "nowrap",
                color: t.fg,
                backgroundColor: t.bg,
            }}
        >
            <MDBox component="span" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: t.fg }} />
            {label}
        </MDBox>
    );
}

// Lucide ikon bileşeni ya da Material Icons adı (string) kabul eder
export function IconGlyph({ icon, color, size = 18 }) {
    if (!icon) return null;
    if (typeof icon === "string") {
        return <Icon sx={{ color, fontSize: `${size}px !important` }}>{icon}</Icon>;
    }
    const Glyph = icon;
    // MUI ikonları (muiName taşır) sx ile, lucide ikonları size/color ile boyutlanır
    if (Glyph.muiName) return <Glyph sx={{ fontSize: `${size}px !important`, color }} />;
    return <Glyph size={size} color={color} strokeWidth={2} />;
}

function IconTile({ icon, tone = "neutral", size = 36, iconSize = 18 }) {
    const t = TONES[tone] || TONES.neutral;

    return (
        <MDBox
            sx={{
                width: size,
                height: size,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                backgroundColor: t.bg,
            }}
        >
            <IconGlyph icon={icon} color={t.fg} size={iconSize} />
        </MDBox>
    );
}

// trend: { delta: number, label: string, goodWhenUp?: boolean }
function TrendLine({ trend }) {
    if (!trend || !Number.isFinite(trend.delta)) return null;

    const up = trend.delta > 0;
    const flat = trend.delta === 0;
    const good = flat ? null : up === (trend.goodWhenUp ?? true);
    const color = flat ? UI.muted : good ? UI.green : UI.red;
    const Arrow = flat ? Minus : up ? TrendingUp : TrendingDown;

    return (
        <MDBox display="flex" alignItems="center" gap={0.5} mt={0.75}>
            <Arrow size={14} color={color} />
            <MDTypography sx={{ fontSize: 12, fontWeight: 600, color }}>
                {up ? "+" : ""}
                {trend.delta}
            </MDTypography>
            <MDTypography sx={{ fontSize: 12, color: UI.muted }}>{trend.label}</MDTypography>
        </MDBox>
    );
}

export function KpiCard({ label, value, hint, icon, tone = "neutral", active = false, onClick, trend }) {
    const color = toneColor(tone);

    return (
        <Card
            onClick={onClick}
            sx={{
                ...cardSx,
                p: 2,
                height: "100%",
                cursor: onClick ? "pointer" : "default",
                borderColor: active ? color : UI.cardBorder,
                boxShadow: active ? `inset 0 0 0 1px ${color}` : "none",
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                "&:hover": onClick
                    ? { borderColor: color, boxShadow: "0 4px 14px rgba(15,23,42,0.06)" }
                    : {},
            }}
        >
            <MDBox display="flex" alignItems="center" gap={1.25}>
                {icon && <IconTile icon={icon} tone={tone} />}
                <MDTypography
                    sx={{ fontSize: 12.5, fontWeight: 600, color: UI.muted, letterSpacing: 0.1, lineHeight: 1.3 }}
                >
                    {label}
                </MDTypography>
            </MDBox>

            <MDTypography sx={{ mt: 1.5, fontSize: 28, fontWeight: 700, color: UI.text, lineHeight: 1.1 }}>
                {value}
            </MDTypography>

            {trend ? (
                <TrendLine trend={trend} />
            ) : (
                hint && (
                    <MDTypography sx={{ mt: 0.75, fontSize: 12, color: UI.muted, lineHeight: 1.4 }}>
                        {hint}
                    </MDTypography>
                )
            )}
        </Card>
    );
}

// "Quick Stats" tarzı oran çubuğu
export function ProgressStat({ label, value, total, tone = "teal", onClick }) {
    const pct = total ? Math.round((value / total) * 100) : 0;

    return (
        <MDBox
            onClick={onClick}
            sx={{ cursor: onClick ? "pointer" : "default", "&:hover .ps-label": onClick ? { color: UI.blue } : {} }}
        >
            <MDBox display="flex" justifyContent="space-between" alignItems="baseline" mb={0.6}>
                <MDTypography className="ps-label" sx={{ fontSize: 13, color: UI.muted }}>
                    {label}
                </MDTypography>
                <MDTypography sx={{ fontSize: 13, fontWeight: 700, color: UI.text }}>
                    {pct}%
                    <MDBox component="span" sx={{ ml: 0.75, fontSize: 11.5, fontWeight: 500, color: UI.subtle }}>
                        {value}/{total}
                    </MDBox>
                </MDTypography>
            </MDBox>
            <MDBox sx={{ height: 8, borderRadius: 4, backgroundColor: "#EEF2F6", overflow: "hidden" }}>
                <MDBox
                    sx={{
                        height: "100%",
                        width: `${pct}%`,
                        borderRadius: 4,
                        backgroundColor: toneColor(tone),
                        transition: "width 0.4s ease",
                    }}
                />
            </MDBox>
        </MDBox>
    );
}

export const relativeDays = (date) => {
    const days = dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");
    if (days === 0) return "Today";
    if (days === 1) return "Tomorrow";
    if (days === -1) return "Yesterday";
    return days > 0 ? `in ${days} days` : `${-days} days ago`;
};

// "Recent Activity" tarzı zaman çizelgesi: yaklaşan ETD / ETA hareketleri
export function TimelineList({ items, emptyText = "No upcoming movements" }) {
    if (!items.length) {
        return (
            <MDBox py={4} display="flex" flexDirection="column" alignItems="center" gap={1}>
                <CalendarClock size={28} color={UI.subtle} />
                <MDTypography sx={{ fontSize: 13, color: UI.muted }}>{emptyText}</MDTypography>
            </MDBox>
        );
    }

    return (
        <MDBox display="flex" flexDirection="column" gap={0.5}>
            {items.map((item) => (
                <MDBox
                    key={item.key}
                    onClick={item.onClick}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    p={1}
                    sx={{
                        borderRadius: 1.5,
                        cursor: item.onClick ? "pointer" : "default",
                        "&:hover": item.onClick ? { backgroundColor: "#F8FAFC" } : {},
                    }}
                >
                    <IconTile icon={item.icon} tone={item.tone} size={34} iconSize={16} />
                    <MDBox flex={1} minWidth={0}>
                        <MDTypography
                            sx={{ fontSize: 13, fontWeight: 600, color: UI.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                            {item.title}
                        </MDTypography>
                        <MDTypography
                            sx={{ fontSize: 11.5, color: UI.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                            {item.description}
                        </MDTypography>
                    </MDBox>
                    <MDBox textAlign="right" flexShrink={0}>
                        <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: item.whenColor || UI.text }}>
                            {item.when}
                        </MDTypography>
                        <MDTypography sx={{ fontSize: 11, color: UI.subtle }}>{item.date}</MDTypography>
                    </MDBox>
                </MDBox>
            ))}
        </MDBox>
    );
}

export function KpiGrid({ children }) {
    return (
        <MDBox
            sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    xl: "repeat(5, minmax(0, 1fr))",
                },
            }}
        >
            {children}
        </MDBox>
    );
}

export function Panel({ title, subtitle, action, children, sx = {}, bodySx = {} }) {
    return (
        <Card sx={{ ...cardSx, height: "100%", display: "flex", flexDirection: "column", ...sx }}>
            <MDBox
                px={2.25}
                py={1.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                sx={{ borderBottom: `1px solid ${UI.border}` }}
            >
                <MDBox minWidth={0}>
                    <MDTypography sx={{ fontSize: 15, fontWeight: 700, color: UI.text }}>{title}</MDTypography>
                    {subtitle && (
                        <MDTypography sx={{ fontSize: 12, color: UI.muted }}>{subtitle}</MDTypography>
                    )}
                </MDBox>
                {action}
            </MDBox>
            <MDBox p={2.25} sx={{ flex: 1, ...bodySx }}>
                {children}
            </MDBox>
        </Card>
    );
}

// Operasyonel aksiyon listesi: satır tıklanınca tablo filtrelenir
export function ActionList({ items }) {
    return (
        <MDBox display="flex" flexDirection="column">
            {items.map((item, index) => {
                const t = TONES[item.tone] || TONES.neutral;
                const isZero = !item.count;

                return (
                    <MDBox
                        key={item.key}
                        onClick={item.onClick}
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        py={1.1}
                        px={1}
                        sx={{
                            cursor: item.onClick ? "pointer" : "default",
                            borderRadius: 1.5,
                            borderTop: index === 0 ? "none" : `1px solid ${UI.border}`,
                            backgroundColor: item.active ? t.bg : "transparent",
                            "&:hover": item.onClick ? { backgroundColor: "#F8FAFC" } : {},
                        }}
                    >
                        <IconTile icon={item.icon} tone={item.tone} size={32} iconSize={16} />

                        <MDBox flex={1} minWidth={0}>
                            <MDTypography sx={{ fontSize: 13, fontWeight: 600, color: UI.text, lineHeight: 1.3 }}>
                                {item.label}
                            </MDTypography>
                            {item.description && (
                                <MDTypography sx={{ fontSize: 11.5, color: UI.muted, lineHeight: 1.3 }}>
                                    {item.description}
                                </MDTypography>
                            )}
                        </MDBox>

                        <MDBox
                            sx={{
                                minWidth: 34,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                textAlign: "center",
                                fontSize: 13,
                                fontWeight: 700,
                                color: isZero ? UI.subtle : t.fg,
                                backgroundColor: isZero ? "#F8FAFC" : t.bg,
                            }}
                        >
                            {item.count}
                        </MDBox>
                    </MDBox>
                );
            })}
        </MDBox>
    );
}

// Yatay sıralı bar listesi: çok sayıda grafiğin yerine okunaklı tek görünüm
export function RankList({ data, onSelect, activeValue, color = UI.teal, emptyText = "No data" }) {
    if (!data.length) {
        return <MDTypography sx={{ fontSize: 13, color: UI.muted }}>{emptyText}</MDTypography>;
    }

    const max = Math.max(...data.map((d) => d.count), 1);
    const total = data.reduce((s, d) => s + d.count, 0);

    return (
        <MDBox display="flex" flexDirection="column" gap={1.25}>
            {data.map((d) => {
                const active = activeValue === d.label;

                return (
                    <MDBox
                        key={d.label}
                        onClick={onSelect ? () => onSelect(d.label) : undefined}
                        sx={{
                            cursor: onSelect ? "pointer" : "default",
                            borderRadius: 1,
                            "&:hover .rank-bar": onSelect ? { opacity: 0.85 } : {},
                        }}
                    >
                        <MDBox display="flex" justifyContent="space-between" gap={1} mb={0.4}>
                            <MDTypography
                                title={d.label}
                                sx={{
                                    fontSize: 12.5,
                                    fontWeight: active ? 700 : 500,
                                    color: UI.text,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {d.label}
                            </MDTypography>
                            <MDTypography sx={{ fontSize: 12.5, fontWeight: 600, color: UI.muted, flexShrink: 0 }}>
                                {d.count}
                                <MDBox component="span" sx={{ ml: 0.75, color: UI.subtle, fontWeight: 500 }}>
                                    {total ? Math.round((d.count / total) * 100) : 0}%
                                </MDBox>
                            </MDTypography>
                        </MDBox>
                        <MDBox sx={{ height: 6, borderRadius: 3, backgroundColor: "#EEF2F6", overflow: "hidden" }}>
                            <MDBox
                                className="rank-bar"
                                sx={{
                                    height: "100%",
                                    width: `${(d.count / max) * 100}%`,
                                    borderRadius: 3,
                                    backgroundColor: d.color || (active ? UI.navy : color),
                                    transition: "width 0.3s ease",
                                }}
                            />
                        </MDBox>
                    </MDBox>
                );
            })}
        </MDBox>
    );
}

// Donut yanında okunaklı lejant (renk + metin + adet)
export function LegendList({ data, onSelect, activeValue }) {
    const total = data.reduce((s, d) => s + d.count, 0);

    return (
        <MDBox display="flex" flexDirection="column" gap={0.5}>
            {data.map((d) => (
                <MDBox
                    key={d.label}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    py={0.5}
                    px={0.75}
                    onClick={onSelect ? () => onSelect(d.label) : undefined}
                    sx={{
                        cursor: onSelect ? "pointer" : "default",
                        borderRadius: 1,
                        backgroundColor: activeValue === d.label ? "#F1F5F9" : "transparent",
                        "&:hover": onSelect ? { backgroundColor: "#F8FAFC" } : {},
                    }}
                >
                    <MDBox sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: d.color, flexShrink: 0 }} />
                    <MDTypography sx={{ fontSize: 12.5, color: UI.text, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {d.label}
                    </MDTypography>
                    <MDTypography sx={{ fontSize: 12.5, fontWeight: 700, color: UI.text }}>{d.count}</MDTypography>
                    <MDTypography sx={{ fontSize: 11.5, color: UI.subtle, width: 34, textAlign: "right" }}>
                        {total ? Math.round((d.count / total) * 100) : 0}%
                    </MDTypography>
                </MDBox>
            ))}
        </MDBox>
    );
}

export function MetricRow({ label, value, valueColor = UI.text, strong = false }) {
    return (
        <MDBox display="flex" justifyContent="space-between" alignItems="baseline" py={0.9} sx={{ borderBottom: `1px solid ${UI.border}`, "&:last-of-type": { borderBottom: "none" } }}>
            <MDTypography sx={{ fontSize: 13, color: UI.muted }}>{label}</MDTypography>
            <MDTypography sx={{ fontSize: strong ? 16 : 13.5, fontWeight: strong ? 700 : 600, color: valueColor }}>
                {value}
            </MDTypography>
        </MDBox>
    );
}

// Küçük sekmeli seçici (analiz kırılımları ve trend için)
export function SegmentedTabs({ options, value, onChange }) {
    return (
        <MDBox display="flex" flexWrap="wrap" gap={0.5}>
            {options.map((opt) => {
                const selected = opt.value === value;

                return (
                    <MDBox
                        key={opt.value}
                        component="button"
                        type="button"
                        onClick={() => onChange(opt.value)}
                        sx={{
                            border: `1px solid ${selected ? UI.navy : UI.border}`,
                            backgroundColor: selected ? UI.navy : UI.surface,
                            color: selected ? "#fff" : UI.muted,
                            fontSize: 12,
                            fontWeight: 600,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            "&:hover": selected ? {} : { color: UI.text, borderColor: UI.subtle },
                        }}
                    >
                        {opt.label}
                    </MDBox>
                );
            })}
        </MDBox>
    );
}

export function DashboardToolbar({
    search,
    onSearch,
    placeholder,
    currency,
    onCurrency,
    filters,
    onRemoveFilter,
    onClear,
    resultLabel,
    dateRange,
    onDateRange,
    dateLabel = "Date",
    ratesMissing = false,
}) {
    const inputSx = {
        "& .MuiOutlinedInput-root": { borderRadius: 1.5, background: "#fff" },
    };

    return (
        <Card sx={{ ...cardSx, p: 1.5 }}>
            <MDBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
                <MDBox flex="1 1 320px">
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={placeholder}
                        value={search}
                        onChange={(e) => onSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: UI.subtle }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={inputSx}
                    />
                </MDBox>
                <MDBox sx={{ width: 140 }}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Currency"
                        value={currency}
                        onChange={(e) => onCurrency(e.target.value)}
                        sx={inputSx}
                    >
                        {currencyOptions.map((item) => (
                            <MenuItem key={item} value={item}>
                                {item}
                            </MenuItem>
                        ))}
                    </TextField>
                </MDBox>
                {dateRange && (
                    <MDBox sx={{ width: 190 }}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label={`${dateLabel} range`}
                            value={dateRange.preset}
                            onChange={(e) => onDateRange({ ...dateRange, preset: e.target.value })}
                            sx={inputSx}
                        >
                            {DATE_PRESETS.map((p) => (
                                <MenuItem key={p.value} value={p.value}>
                                    {p.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </MDBox>
                )}
                {dateRange?.preset === "custom" && (
                    <MDBox display="flex" gap={1}>
                        {[
                            ["from", "From"],
                            ["to", "To"],
                        ].map(([key, label]) => (
                            <MuiTextField
                                key={key}
                                type="date"
                                size="small"
                                label={label}
                                value={dateRange[key]}
                                onChange={(e) => onDateRange({ ...dateRange, [key]: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                sx={{ ...inputSx, width: 160 }}
                            />
                        ))}
                    </MDBox>
                )}
            </MDBox>

            {dateRange && dateRange.preset !== "all" && (
                <MDTypography sx={{ fontSize: 11.5, color: UI.muted, mt: 0.75 }}>
                    Files without a {dateLabel} date are hidden while a date range is selected.
                </MDTypography>
            )}

            {ratesMissing && (
                <MDBox
                    mt={1.25}
                    px={1.5}
                    py={0.75}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ borderRadius: 1.5, backgroundColor: TONES.warning.bg }}
                >
                    <AlertTriangle size={15} color={TONES.warning.fg} />
                    <MDTypography sx={{ fontSize: 12, color: TONES.warning.fg, fontWeight: 600 }}>
                        Exchange rates could not be loaded. Amounts in other currencies are shown unconverted.
                    </MDTypography>
                </MDBox>
            )}

            {filters.length > 0 && (
                <MDBox display="flex" alignItems="center" gap={1} mt={1.25} flexWrap="wrap">
                    <MDTypography sx={{ fontSize: 12, color: UI.muted, fontWeight: 600 }}>Active filters:</MDTypography>
                    {filters.map((filter) => (
                        <Chip
                            key={`${filter.type}-${filter.value || "all"}`}
                            size="small"
                            label={filter.label}
                            onDelete={() => onRemoveFilter(filter)}
                            sx={{ fontWeight: 600, borderRadius: 1, backgroundColor: "#EEF2F6", color: UI.text }}
                        />
                    ))}
                    {resultLabel && (
                        <MDTypography sx={{ fontSize: 12, color: UI.muted }}>· {resultLabel}</MDTypography>
                    )}
                    <MDBox
                        component="button"
                        type="button"
                        onClick={onClear}
                        sx={{ border: "none", background: "none", color: UI.blue, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                    >
                        Clear all
                    </MDBox>
                </MDBox>
            )}
        </Card>
    );
}

export const baseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        // CRM dashboard'larının global kaydettiği eklentiler (donut ortası yazı + gölge) burada çift çizim yapıyordu
        centerText: false,
        shadow: false,
        legend: { display: false },
        tooltip: {
            backgroundColor: UI.navy,
            padding: 10,
            cornerRadius: 6,
            titleFont: { weight: "600" },
        },
    },
};

export const barChartOptions = {
    ...baseChartOptions,
    scales: {
        x: {
            grid: { display: false },
            border: { display: false },
            ticks: { color: UI.muted, font: { size: 11 } },
        },
        y: {
            beginAtZero: true,
            grid: { color: "#EEF2F6" },
            border: { display: false },
            ticks: { color: UI.muted, font: { size: 11 }, precision: 0 },
        },
    },
};

// Donut + ortada toplam + yanında lejant
export function DonutWithLegend({ data, totalLabel = "Total", onSelect, activeValue }) {
    const total = data.reduce((s, d) => s + d.count, 0);

    return (
        <MDBox display="flex" flexDirection="column" alignItems="center" gap={2}>
            <MDBox position="relative" sx={{ width: 160, height: 160, flexShrink: 0 }}>
                <Doughnut
                    data={{
                        labels: data.map((d) => d.label),
                        datasets: [
                            {
                                data: data.map((d) => d.count),
                                backgroundColor: data.map((d) => d.color),
                                borderWidth: 2,
                                borderColor: "#fff",
                            },
                        ],
                    }}
                    options={donutChartOptions(onSelect ? (i) => onSelect(data[i]?.label) : null)}
                />
                <MDBox
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ pointerEvents: "none" }}
                >
                    <MDTypography sx={{ fontSize: 26, fontWeight: 700, color: UI.text, lineHeight: 1 }}>{total}</MDTypography>
                    <MDTypography sx={{ fontSize: 11.5, color: UI.muted }}>{totalLabel}</MDTypography>
                </MDBox>
            </MDBox>
            <MDBox flex={1} minWidth={0} width="100%">
                <LegendList data={data} onSelect={onSelect} activeValue={activeValue} />
            </MDBox>
        </MDBox>
    );
}

export const donutChartOptions = (onSelectIndex) => ({
    ...baseChartOptions,
    cutout: "70%",
    onClick: (_event, elements) => {
        if (elements?.length && onSelectIndex) onSelectIndex(elements[0].index);
    },
    onHover: (event, elements) => {
        if (event?.native?.target) {
            event.native.target.style.cursor = elements?.length && onSelectIndex ? "pointer" : "default";
        }
    },
});

// Tablonun son sütunundaki detay düğmesi; nokta rengi önceliği gösterir
export function DetailsButton({ priority, hint, onClick }) {
    return (
        <Tooltip title={hint || `${priority} priority`} arrow>
            <MDBox
                component="button"
                type="button"
                onClick={onClick}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    border: `1px solid ${UI.border}`,
                    borderRadius: 1.5,
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: UI.text,
                    whiteSpace: "nowrap",
                    "&:hover": { borderColor: UI.blue, color: UI.blue },
                }}
            >
                <MDBox
                    component="span"
                    sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: toneColor(priorityTone(priority)) }}
                />
                Details
                <PanelRightOpen size={14} />
            </MDBox>
        </Tooltip>
    );
}

// Alt alta etiketli satırlar (Origin / Destination, Line / Vessel gibi)
export function StackedCell({ lines, labelWidth = 78, max = 40 }) {
    return (
        <MDBox>
            {lines.map(([label, value]) => (
                <MDTypography
                    key={label}
                    component="div"
                    sx={{ fontSize: 13, color: UI.text, fontWeight: 500, lineHeight: 1.45, whiteSpace: "nowrap" }}
                >
                    <MDBox
                        component="span"
                        sx={{ display: "inline-block", width: labelWidth, color: UI.muted, fontSize: 11.5, fontWeight: 600 }}
                    >
                        {label}
                    </MDBox>
                    <TruncatedText text={value} max={max} />
                </MDTypography>
            ))}
        </MDBox>
    );
}

// Tablo kartı: başlık + filtre özeti + DataTable
export function TableCard({ cardRef, title, summary, onExport, children }) {
    return (
        <Card ref={cardRef} sx={{ ...cardSx, overflow: "hidden" }}>
            <MDBox
                px={2.25}
                py={1.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1.5}
                sx={{ borderBottom: `1px solid ${UI.border}` }}
            >
                <MDBox minWidth={0}>
                    <MDTypography sx={{ fontSize: 15, fontWeight: 700, color: UI.text }}>{title}</MDTypography>
                    <MDTypography sx={{ fontSize: 12, color: UI.muted }}>{summary}</MDTypography>
                </MDBox>
                {onExport && (
                    <MDBox
                        component="button"
                        type="button"
                        onClick={onExport}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.5,
                            py: 0.75,
                            flexShrink: 0,
                            border: `1px solid ${UI.cardBorder}`,
                            borderRadius: 1.5,
                            backgroundColor: "#fff",
                            cursor: "pointer",
                            fontFamily: "inherit",
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: UI.text,
                            "&:hover": { borderColor: UI.blue, color: UI.blue },
                        }}
                    >
                        <Download size={15} />
                        Export CSV
                    </MDBox>
                )}
            </MDBox>
            {children}
        </Card>
    );
}

// Ocean / Air / Road sayfaları için ortak başlık + HFL/MBL sekme kabuğu
export function ControlTowerPage({ title, subtitle, tabs, embedded = false }) {
    const [activeTab, setActiveTab] = useState(0);

    const content = (
        <>
            {!embedded && <DashboardNavbar />}

            <MDBox pt={embedded ? 0 : 3} pb={3} sx={{ width: "100%" }}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "flex-end" }}
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={2}
                    mb={2}
                >
                    <MDBox>
                        <MDTypography sx={{ fontSize: 22, fontWeight: 700, color: UI.text, letterSpacing: "-0.2px" }}>
                            {title}
                        </MDTypography>
                        <MDTypography sx={{ fontSize: 13, color: UI.muted, mt: 0.25 }}>{subtitle}</MDTypography>
                    </MDBox>

                    <MDBox
                        role="tablist"
                        display="inline-flex"
                        sx={{ p: 0.5, gap: 0.5, borderRadius: 2, backgroundColor: "#fff", border: `1px solid ${UI.cardBorder}` }}
                    >
                        {tabs.map((tab, index) => {
                            const selected = index === activeTab;

                            return (
                                <MDBox
                                    key={tab.label}
                                    component="button"
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    onClick={() => setActiveTab(index)}
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        px: 1.75,
                                        py: 0.9,
                                        border: "none",
                                        borderRadius: 1.5,
                                        cursor: "pointer",
                                        fontFamily: "inherit",
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: selected ? "#fff" : UI.muted,
                                        backgroundColor: selected ? UI.navy : "transparent",
                                        "&:hover": selected ? {} : { color: UI.text, backgroundColor: "#F1F5F9" },
                                    }}
                                >
                                    <IconGlyph icon={tab.icon} size={16} />
                                    {tab.label}
                                </MDBox>
                            );
                        })}
                    </MDBox>
                </MDBox>

                {tabs[activeTab]?.component}
            </MDBox>

            {!embedded && <Footer />}
        </>
    );

    if (embedded) return content;

    return <DashboardLayout>{content}</DashboardLayout>;
}
